import type { KnockGuideStep } from "@knocklabs/client";
import { useGuides } from "@knocklabs/react";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { KnockErrorBoundary } from "@src/appV2/Knock/components/KnockErrorBoundary";
import { useKnockMarkAsSeen } from "@src/appV2/Knock/hooks/useKnockMarkAsSeen";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { useOpenUrl } from "@src/mobile/utils/openUrl";
import { useEffect, useRef } from "react";

import { REVIEW_PROMPT_GUIDE_TYPE, REVIEW_PROMPT_SUPPORT_URL } from "./constants";
import { normalizeContent } from "./normalizeContent";
import { ReviewPromptGuideView, type ReviewPromptGuideViewContent } from "./ReviewPromptGuideView";
import { useRequestInAppReview } from "./useRequestInAppReview";
import { useReviewPromptCooldowns } from "./useReviewPromptCooldowns";

interface ReviewPromptItemProps {
  content: ReviewPromptGuideViewContent;
  guideKey: string;
  step: KnockGuideStep;
  workplaceId?: string;
  workplaceName?: string;
}

function ReviewPromptItem(props: ReviewPromptItemProps) {
  const { content, guideKey, step, workplaceId, workplaceName } = props;
  const { requestInAppReview } = useRequestInAppReview();
  const { identifyDeclineCooldown, identifyDismissCooldown } = useReviewPromptCooldowns();
  const openUrl = useOpenUrl();
  const modalState = useModalState(ModalStatus.OPEN);

  useKnockMarkAsSeen(step);

  // Archive is server-side terminal in Knock — once `archived_at` is set, the
  // user is permanently excluded from the guide's eligible set, regardless of
  // targeting. So only the Yes path archives. Non-terminal paths (No, Later,
  // feedback) hide the dialog via `modalState.closeModal()` and gate future
  // visibility through the dashboard targeting rule + the cooldown attribute
  // writes in `useReviewPromptCooldowns`.

  const hasLoggedViewed = useRef<string>();
  useEffect(() => {
    if (!hasLoggedViewed.current || hasLoggedViewed.current !== guideKey) {
      logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_SHOWN, { guideKey, workplaceId, workplaceName });
      hasLoggedViewed.current = guideKey;
    }
  }, [guideKey, workplaceId, workplaceName]);

  return (
    <ReviewPromptGuideView
      content={content}
      modalState={modalState}
      onYesClick={async () => {
        step.markAsInteracted({ metadata: { action: "yes" } });
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_YES_CLICKED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        // Close the dialog before kicking off the native call so the user
        // doesn't see our dialog and the native sheet stacked. The native
        // sheet animates in over the WebView and `requestInAppReview` swallows
        // its own failures, so archiving after the await is safe.
        modalState.closeModal();
        await requestInAppReview();
        step.markAsArchived();
      }}
      onNoClick={() => {
        // markAsInteracted defers to submit/dismiss so the engagement record
        // is written exactly once, with whatever feedback text we have.
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_NO_CLICKED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        // Cooldown + count are written here so a user who bails (closes the
        // app) before Submit/Cancel is still excluded from re-prompts.
        void identifyDeclineCooldown();
      }}
      onLaterClick={async () => {
        step.markAsInteracted({ metadata: { action: "later" } });
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_LATER_CLICKED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        modalState.closeModal();
        await identifyDismissCooldown();
      }}
      onFeedbackSubmit={(feedback) => {
        step.markAsInteracted({ metadata: { action: "no", feedback } });
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_FEEDBACK_SUBMITTED, {
          guideKey,
          workplaceId,
          workplaceName,
          feedback,
          feedbackLength: feedback.length,
        });
        modalState.closeModal();
      }}
      onFeedbackDismiss={() => {
        // Declined without elaborating — still counts as a decline; cooldown
        // was already written on the No tap.
        step.markAsInteracted({ metadata: { action: "no" } });
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_FEEDBACK_DISMISSED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        modalState.closeModal();
      }}
      onContactSupport={async () => {
        // The in-app browser opens over the SPA; the dialog stays mounted
        // underneath so the user can still pick Submit / Cancel when they
        // return. Don't archive, don't write any cooldown attribute.
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_CONTACT_SUPPORT_CLICKED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        await openUrl(REVIEW_PROMPT_SUPPORT_URL);
      }}
    />
  );
}

interface ReviewPromptGuideInnerProps {
  workplaceId?: string;
  workplaceName?: string;
}

function ReviewPromptGuideInner(props: ReviewPromptGuideInnerProps) {
  const { workplaceId, workplaceName } = props;
  // Kill-switch gate at the root of the surface so the dialog never mounts
  // when the flag is off — even if a Knock dashboard guide somehow targets
  // through. The hook holds the belt-and-suspenders check for the native call.
  const isEnabled = useCbhFlag(CbhFeatureFlag.WORKPLACE_RATING_PROMPT, { defaultValue: false });
  const { guides } = useGuides({ type: REVIEW_PROMPT_GUIDE_TYPE });

  if (!isEnabled || guides.length === 0) {
    return null;
  }

  const guide = guides[0];
  const step = guide?.getStep();
  const content = normalizeContent(step?.content);

  if (!guide || !step || !content) {
    return null;
  }

  return (
    // Keyed on guide.key so the view's internal step + feedback-text state
    // resets if a different Knock guide takes the slot mid-session.
    <ReviewPromptItem
      key={guide.key}
      content={content}
      guideKey={guide.key}
      step={step}
      workplaceId={workplaceId}
      workplaceName={workplaceName}
    />
  );
}

interface ReviewPromptGuideProps {
  /**
   * Active workplace context for analytics. Sourced by the parent container
   * from session state (workplace user id and profile name) so V2 stays
   * Redux-free.
   */
  workplaceId?: string;
  workplaceName?: string;
}

/**
 * App-root rating-prompt surface. Filters Knock guides on the custom
 * `app-store-review-soft-prompt` message type and renders the mood step. Tapping "Not really"
 * transitions the same dialog to a feedback step (subtitle + textarea +
 * Submit / Contact support / Cancel). Submit, Cancel, and "Ask me later"
 * each write a Knock user attribute that the dashboard uses to enforce the
 * cooldown window. Contact support opens the support URL in the in-app
 * browser and leaves the dialog mounted underneath.
 */
export function ReviewPromptGuide(props: ReviewPromptGuideProps) {
  const { workplaceId, workplaceName } = props;
  return (
    <KnockErrorBoundary>
      <ReviewPromptGuideInner workplaceId={workplaceId} workplaceName={workplaceName} />
    </KnockErrorBoundary>
  );
}
